import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Camera, Eraser, UserCheck, Search, X, Lightbulb } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useStore } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, fmtEuro } from "@/lib/status";
import { COMMON_FAULTS } from "@/lib/faults";

const CONDITIONS = ["Graffi display", "Graffi scocca", "Ammaccature", "Vetro posteriore rotto",
  "Fotocamera danneggiata", "Porta USB danneggiata", "Face ID non funzionante"];
const ACCESSORIES = ["Caricabatterie", "Cavo", "Cover", "SIM", "Scheda SD", "Altro"];
const STEPS = ["Cliente", "Dispositivo", "Problema & Check-in", "Foto & Firma"];

export default function NewRepair() {
  const { stores } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [custQuery, setCustQuery] = useState("");
  const [results, setResults] = useState([]);
  const [custDevices, setCustDevices] = useState([]);
  const [custRepairs, setCustRepairs] = useState([]);
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [staff, setStaff] = useState([]);

  const [f, setF] = useState({
    store_id: user?.store_id || "",
    customer_id: null,
    customer: { first_name: "", last_name: "", phone: "", email: "", tax_code: "", notes: "", whatsapp_opt_in: true },
    device: { category: "Smartphone", brand: "", model: "", imei: "", serial_number: "", color: "", storage: "", device_password: "" },
    problem_description: "", internal_notes: "",
    conditions: [], accessories: [], photos: [], signature: null,
    assigned_user_id: user?.id || null,
  });

  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!f.store_id && stores.length) setF((s) => ({ ...s, store_id: stores[0].id }));
  }, [stores]);

  const setCust = (k, v) => setF((s) => ({ ...s, customer: { ...s.customer, [k]: v } }));
  const setDev = (k, v) => setF((s) => ({ ...s, device: { ...s.device, [k]: v } }));

  useEffect(() => {
    const q = custQuery.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q } });
        setResults(data.slice(0, 6));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const deriveDevices = (repairs) => {
    const seen = new Map();
    for (const r of repairs || []) {
      const d = r.device; if (!d) continue;
      const key = `${d.brand}|${d.model}|${d.imei || ""}|${d.serial_number || ""}`.toLowerCase();
      if (!seen.has(key)) seen.set(key, d);
    }
    return [...seen.values()];
  };

  const linkCustomer = async (c) => {
    setF((s) => ({ ...s, customer_id: c.id, customer: {
      first_name: c.first_name, last_name: c.last_name, phone: c.phone,
      email: c.email || "", tax_code: c.tax_code || "", notes: c.notes || "",
      whatsapp_opt_in: c.whatsapp_opt_in !== false } }));
    setCustQuery(""); setResults([]);
    toast.success("Cliente collegato — modifica i campi per aggiornarne i dati");
    try {
      const { data } = await api.get(`/customers/${c.id}`);
      setCustRepairs((data.repairs || []).slice(0, 5));
      setCustDevices(deriveDevices(data.repairs));
    } catch { setCustRepairs([]); setCustDevices([]); }
  };

  const selectDevice = (d) => {
    setF((s) => ({ ...s, device: {
      category: d.category || "Smartphone", brand: d.brand || "", model: d.model || "",
      imei: d.imei || "", serial_number: d.serial_number || "", color: d.color || "",
      storage: d.storage || "", device_password: d.device_password || "" } }));
    toast.success("Dispositivo selezionato");
  };

  const isSameDevice = (d) => f.device.brand === d.brand && f.device.model === d.model
    && (f.device.imei || "") === (d.imei || "") && (f.device.serial_number || "") === (d.serial_number || "");

  const unlinkCustomer = () => {
    setF((s) => ({ ...s, customer_id: null, customer: { first_name: "", last_name: "", phone: "", email: "", tax_code: "", notes: "", whatsapp_opt_in: true } }));
    setCustQuery(""); setResults([]); setCustDevices([]); setCustRepairs([]);
    toast.message("Cliente scollegato");
  };

  const addFault = (fault) => setF((s) => {
    const cur = s.problem_description.trim();
    const parts = cur.split(/[,\n]/).map((x) => x.trim().toLowerCase());
    if (parts.includes(fault.toLowerCase())) return s;
    return { ...s, problem_description: cur ? `${cur}, ${fault}` : fault };
  });

  useEffect(() => {
    const brand = f.device.brand, model = f.device.model;
    if (!brand || !model) { setPriceSuggestion(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/repairs/price-suggestion", { params: { brand, model } });
        setPriceSuggestion(data.found ? data : null);
      } catch { setPriceSuggestion(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [f.device.brand, f.device.model]);

  const toggle = (field, val) => setF((s) => ({
    ...s, [field]: s[field].includes(val) ? s[field].filter((x) => x !== val) : [...s[field], val],
  }));

  const onPhoto = (e, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF((s) => ({ ...s, photos: [...s.photos, { category, url: reader.result }] }));
    reader.readAsDataURL(file);
  };

  // signature canvas
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setF((s) => ({ ...s, signature: null })); };
  const saveSig = () => setF((s) => ({ ...s, signature: canvasRef.current.toDataURL("image/png") }));

  const canNext = () => {
    if (step === 0) return f.customer.first_name && f.customer.last_name && f.customer.phone && f.store_id;
    if (step === 1) return f.device.brand && f.device.model;
    if (step === 2) return f.problem_description;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/repairs", f);
      toast.success(`Riparazione ${data.repair_number} creata`);
      navigate(`/repairs/${data.id}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Nuova riparazione</h1>

      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
              i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-secondary text-muted-foreground"
            }`}>
              {i < step ? <Check className="h-3.5 w-3.5" /> : <span>{i + 1}</span>} {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
          </React.Fragment>
        ))}
      </div>

      <Card className="p-6">
        {step === 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Negozio *</Label>
              <Select value={f.store_id} onValueChange={(v) => setF((s) => ({ ...s, store_id: v }))}>
                <SelectTrigger data-testid="intake-store"><SelectValue placeholder="Seleziona negozio" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tecnico assegnato</Label>
              <Select value={f.assigned_user_id || "none"} onValueChange={(v) => setF((s) => ({ ...s, assigned_user_id: v === "none" ? null : v }))}>
                <SelectTrigger data-testid="intake-technician"><SelectValue placeholder="Nessun tecnico" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun tecnico</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cerca cliente esistente</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="intake-customer-search" className="pl-9" value={custQuery}
                  onChange={(e) => setCustQuery(e.target.value)} placeholder="Nome, telefono o email…" />
                {results.length > 0 && (
                  <div data-testid="customer-search-results" className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                    {results.map((c) => (
                      <button key={c.id} type="button" data-testid={`customer-result-${c.id}`} onClick={() => linkCustomer(c)}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {c.first_name?.[0]}{c.last_name?.[0]}
                        </span>
                        <span className="flex-1">
                          <b>{c.first_name} {c.last_name}</b>
                          <span className="block text-xs text-muted-foreground">{c.phone}{c.email ? ` · ${c.email}` : ""}</span>
                        </span>
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{c.repair_count} rip.</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {f.customer_id && (
              <div data-testid="linked-customer-banner" className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                <UserCheck className="h-4 w-4 shrink-0" />
                <span className="flex-1">Cliente collegato: <b>{f.customer.first_name} {f.customer.last_name}</b>. Le modifiche ai campi ne aggiorneranno la scheda.</span>
                <button type="button" data-testid="unlink-customer" onClick={unlinkCustomer} className="rounded p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"><X className="h-4 w-4" /></button>
              </div>
            )}

            {f.customer_id && custRepairs.length > 0 && (
              <div data-testid="customer-history" className="rounded-lg border border-border">
                <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ultime riparazioni del cliente</div>
                <div className="divide-y divide-border">
                  {custRepairs.map((r) => (
                    <div key={r.id} data-testid={`history-repair-${r.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="font-mono text-xs font-semibold text-primary">{r.repair_number}</span>
                      <span className="flex-1 truncate">{r.device?.brand} {r.device?.model}</span>
                      <StatusBadge code={r.status_code} />
                      <span className="hidden text-xs text-muted-foreground sm:block">{fmtDate(r.received_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Telefono *</Label>
              <Input data-testid="intake-phone" value={f.customer.phone} onChange={(e) => setCust("phone", e.target.value)} placeholder="+39 333 1234567" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Nome *</Label>
                <Input data-testid="intake-firstname" value={f.customer.first_name} onChange={(e) => setCust("first_name", e.target.value)} /></div>
              <div className="space-y-2"><Label>Cognome *</Label>
                <Input data-testid="intake-lastname" value={f.customer.last_name} onChange={(e) => setCust("last_name", e.target.value)} /></div>
              <div className="space-y-2"><Label>Email</Label>
                <Input value={f.customer.email} onChange={(e) => setCust("email", e.target.value)} /></div>
              <div className="space-y-2"><Label>Codice fiscale</Label>
                <Input value={f.customer.tax_code} onChange={(e) => setCust("tax_code", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.customer.whatsapp_opt_in} onCheckedChange={(v) => setCust("whatsapp_opt_in", !!v)} />
              Consenso a ricevere aggiornamenti via WhatsApp
            </label>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            {custDevices.length > 0 && (
              <div data-testid="known-devices" className="space-y-2">
                <Label>Dispositivi già registrati del cliente</Label>
                <div className="flex flex-wrap gap-2">
                  {custDevices.map((d, i) => (
                    <button key={i} type="button" data-testid={`known-device-${i}`} onClick={() => selectDevice(d)}
                      className={`min-w-[160px] rounded-lg border px-3 py-2 text-left text-sm transition-colors ${isSameDevice(d) ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"}`}>
                      <span className="block font-semibold">{d.brand} {d.model}</span>
                      <span className="block text-xs text-muted-foreground">{d.imei ? `IMEI ${d.imei}` : d.serial_number ? `SN ${d.serial_number}` : (d.category || "—")}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Seleziona un dispositivo per compilare automaticamente i campi, oppure inseriscine uno nuovo.</p>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Categoria</Label>
                <Input value={f.device.category} onChange={(e) => setDev("category", e.target.value)} /></div>
              <div className="space-y-2"><Label>Marca *</Label>
                <Input data-testid="intake-brand" value={f.device.brand} onChange={(e) => setDev("brand", e.target.value)} placeholder="Apple" /></div>
              <div className="space-y-2"><Label>Modello *</Label>
                <Input data-testid="intake-model" value={f.device.model} onChange={(e) => setDev("model", e.target.value)} placeholder="iPhone 15 Pro" /></div>
              <div className="space-y-2"><Label>IMEI</Label>
                <Input value={f.device.imei} onChange={(e) => setDev("imei", e.target.value)} /></div>
              <div className="space-y-2"><Label>Seriale</Label>
                <Input value={f.device.serial_number} onChange={(e) => setDev("serial_number", e.target.value)} /></div>
              <div className="space-y-2"><Label>Colore</Label>
                <Input value={f.device.color} onChange={(e) => setDev("color", e.target.value)} /></div>
              <div className="space-y-2"><Label>Memoria</Label>
                <Input value={f.device.storage} onChange={(e) => setDev("storage", e.target.value)} /></div>
              <div className="space-y-2"><Label>Password dispositivo</Label>
                <Input value={f.device.device_password} onChange={(e) => setDev("device_password", e.target.value)} /></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {priceSuggestion && (
              <div data-testid="price-suggestion" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Preventivo suggerito per <b>{f.device.brand} {f.device.model}</b>: <b data-testid="suggested-price">{fmtEuro(priceSuggestion.total)}</b>{priceSuggestion.problem ? <span className="text-muted-foreground"> — ultima riparazione simile: “{priceSuggestion.problem}” ({priceSuggestion.repair_number})</span> : null}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label>Problema dichiarato *</Label>
              <div className="flex flex-wrap gap-2" data-testid="quick-faults">
                {COMMON_FAULTS.map((fault, i) => (
                  <button key={fault} type="button" data-testid={`quick-fault-${i}`} onClick={() => addFault(fault)}
                    className="rounded-full border border-border px-3 py-1 text-xs transition-colors hover:border-primary hover:bg-primary/10">
                    + {fault}
                  </button>
                ))}
              </div>
              <Input data-testid="intake-problem" value={f.problem_description} onChange={(e) => setF((s) => ({ ...s, problem_description: e.target.value }))} placeholder="Es. Schermo rotto" />
            </div>
            <div className="space-y-2"><Label>Note interne</Label>
              <Textarea value={f.internal_notes} onChange={(e) => setF((s) => ({ ...s, internal_notes: e.target.value }))} /></div>
            <div>
              <Label className="mb-2 block">Condizioni alla consegna</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {CONDITIONS.map((c) => (
                  <label key={c} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                    <Checkbox checked={f.conditions.includes(c)} onCheckedChange={() => toggle("conditions", c)} /> {c}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Accessori consegnati</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {ACCESSORIES.map((c) => (
                  <label key={c} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                    <Checkbox checked={f.accessories.includes(c)} onCheckedChange={() => toggle("accessories", c)} /> {c}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <Label className="mb-2 block">Foto dispositivo</Label>
              <div className="flex flex-wrap gap-3">
                {f.photos.map((p, i) => (
                  <img key={i} src={p.url} alt="" className="h-20 w-20 rounded-lg border border-border object-cover" />
                ))}
                <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary">
                  <Camera className="h-6 w-6" />
                  <input type="file" accept="image/*" className="hidden" data-testid="intake-photo" onChange={(e) => onPhoto(e, "FRONT")} />
                </label>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Firma del cliente</Label>
              <p className="mb-2 text-xs text-muted-foreground">Il cliente dichiara di aver consegnato il dispositivo nelle condizioni indicate.</p>
              <canvas ref={canvasRef} width={620} height={180} data-testid="signature-canvas"
                className="w-full touch-none rounded-lg border border-border bg-white"
                onMouseDown={start} onMouseMove={move} onMouseUp={() => { end(); saveSig(); }} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={() => { end(); saveSig(); }} />
              <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={clearSig}>
                <Eraser className="mr-2 h-4 w-4" /> Cancella firma
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <Button variant="outline" data-testid="wizard-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Indietro
          </Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}>
              Avanti <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button data-testid="wizard-submit" disabled={saving} onClick={submit}>
              {saving ? "Salvataggio…" : "Registra riparazione"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
